'use client'

import Link from 'next/link'

import { ContentImage } from '@/components/ui/content-image'
import { Icon } from '@/components/ui/icon'
import { ServiceItem } from '@/lib/types/operations/service'
import { cn } from '@/lib/utils'

interface Props {
  service: ServiceItem
  categoryName?: string
}

function formatDuration(minutes: number | null | undefined) {
  if (!minutes) return null
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return mins > 0 ? `${hours}h ${mins}min` : `${hours}hr${hours > 1 ? 's' : ''}`
}

// PARTIAL_WRAP -> "Partial Wrap"
function humanizeEnum(value: string) {
  return value
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Whole dollars with locale grouping: 2776 -> "$2,776"
function formatDollars(price: number) {
  return `$${new Intl.NumberFormat('en-CA', { maximumFractionDigits: 0 }).format(price)}`
}

function formatPrice(service: ServiceItem) {
  const price = service.price || 0
  if (service.pricingType === 'QUOTE') return 'Get a Quote'
  if (service.pricingType === 'HOURLY') return `${formatDollars(price)}/hr`
  return formatDollars(price)
}

// service.price is real catalog data from getServicesData (Prisma), the same
// figure this card has always shown: not an estimator import and not
// invented copy, so spec section 5.3's "no invented dollar figure" rule does
// not apply to it. It is styled with `text-foreground` below, not the accent
// colour: the v3 teal pairing rule (spec 3.1) is a fill/text pairing (teal
// fill carries white text), not a rule about accent as a text colour on the
// paper/white surface this card sits on, so `text-accent` stays out of the
// price regardless.
export function ServiceCardRedesigned({ service, categoryName }: Props) {
  const slug = service.slug || service.id
  const duration = formatDuration(service.duration)
  const price = formatPrice(service)
  const hasPromo = service.promotionalPrice && service.promotionalPrice < (service.price || 0)

  return (
    <Link
      href={`/services/${slug}`}
      data-testid='service-card'
      className='group block overflow-hidden rounded-2xl border border-border bg-surface transition-colors'
    >
      {/* Image */}
      <div className='relative aspect-[16/10] overflow-hidden bg-[var(--surface-secondary)]'>
        <ContentImage
          src={service.media?.[0]?.url}
          alt={service.name}
          contentType='SERVICE'
          className='w-full h-full object-cover transition-transform duration-300 group-hover:scale-105'
          fallbackSrc='/images/placeholder-service.jpg'
        />
        {categoryName && (
          <span className='absolute top-3 left-3 rounded-full bg-black/50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[1px] text-white backdrop-blur-sm'>
            {categoryName}
          </span>
        )}
        {hasPromo && service.promotionPercentage && service.promotionPercentage > 0 && (
          <span className='absolute top-3 right-3 rounded-full bg-accent px-2.5 py-1 text-[10px] font-bold uppercase tracking-[1px] text-accent-foreground'>
            {service.promotionPercentage}% Off
          </span>
        )}
      </div>

      {/* Content */}
      <div className='p-5'>
        <h3 className='cw-display-3 line-clamp-2 text-foreground'>
          {service.name}
        </h3>

        {service.description && (
          <p
            title={service.description}
            className='mt-2 text-xs leading-relaxed text-muted line-clamp-3 lg:line-clamp-2'
          >
            {service.description}
          </p>
        )}

        {/* Meta row */}
        <div className='mt-3 flex items-center gap-3 text-xs text-muted'>
          {duration && (
            <span className='flex items-center gap-1'>
              <Icon icon='cw-clock' size='sm' />
              {duration}
            </span>
          )}
          {service.complexity && (
            <span className='flex items-center gap-1'>
              <Icon icon='cw-settings' size='sm' />
              {humanizeEnum(service.complexity)}
            </span>
          )}
        </div>

        {/* Price + CTA */}
        <div className='mt-4 flex items-center justify-between'>
          <div>
            {hasPromo ? (
              <div className='flex items-baseline gap-2'>
                <span className='text-lg font-bold text-foreground'>
                  {formatDollars(service.promotionalPrice!)}
                </span>
                <span className='text-xs text-muted line-through'>
                  {formatDollars(service.price ?? 0)}
                </span>
              </div>
            ) : (
              <span className={cn(
                'text-lg font-bold text-foreground',
                service.pricingType === 'QUOTE' && 'text-sm'
              )}>
                {price}
              </span>
            )}
          </div>

          <span className='flex items-center gap-2 text-xs font-medium text-foreground'>
            View Details
            <span
              aria-hidden='true'
              data-testid='service-card-cta'
              className='inline-flex h-8 w-8 items-center justify-center rounded-full bg-[var(--surface-secondary)] text-foreground transition-colors group-hover:bg-accent group-hover:text-accent-foreground'
            >
              <Icon icon='cw-arrow-right' size='sm' />
            </span>
          </span>
        </div>
      </div>
    </Link>
  )
}
